/**
 * `/course/$courseId` — the learner course screen (SCH Milestone A).
 *
 * Header from `learning.courses.get` (title, kind badge, description, back-link
 * to `/catalog`), then the "Tests & quizzes" section from
 * `learning.quizzes.listForLearner` — published quizzes only, metadata + question
 * count, never question content. Each quiz row carries a primary "Start" →
 * `/quiz/$quizId` (the runner; `attempts.start` enforces active enrollment /
 * attempt limit / availability server-side, so no enrollment pre-check here) and
 * the caller's own prior attempts via `learning.attempts.list`, each linking to
 * `/result/$attemptId`.
 *
 * Kind vocabulary is inlined (mirrors `COURSE_KINDS` in the learning router /
 * `@mangrove/core/learning`) — this SPA must never import `@mangrove/core`.
 */
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Clock,
  Dumbbell,
  Flame,
  GraduationCap,
  Hash,
  History,
  Hourglass,
  Layers,
  LayoutDashboard,
  ListChecks,
  MessageCircle,
  PlayCircle,
  Repeat,
  Target,
  Trophy,
} from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  Separator,
  Skeleton,
  Tabs,
  type TabItem,
  cn,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';
import { MentorDock } from '../components/mentor/mentor-dock.js';
import { CourseModules } from '../components/course/course-modules.js';
import { useResolvedFeatures } from '../lib/features.js';

export const Route = createFileRoute('/course/$courseId')({
  component: CoursePage,
});

type RouterOutputs = inferRouterOutputs<AppRouter>;
type Quiz = RouterOutputs['learning']['quizzes']['listForLearner']['quizzes'][number];
type Attempt = RouterOutputs['learning']['attempts']['list']['attempts'][number];

/** Mirrors COURSE_KINDS — 'academic' | 'training' | 'onboarding' | 'compliance'. */
const KIND_LABEL: Record<string, string> = {
  academic: 'Academic',
  training: 'Training',
  onboarding: 'Onboarding',
  compliance: 'Compliance',
};

function kindLabelOf(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

// P-SCH-V1 (course-centric hub): one page per course with everything inside —
// Overview (lightweight progress) · Materials · Quizzes & Practice · Results —
// instead of the cross-course global nav pages (those still exist by URL, just
// hidden from the nav). Local tab state (default Overview); no URL sync yet.
type CourseTab = 'overview' | 'materials' | 'quizzes' | 'results';
// SCH-I3-C5: Overview → "Dashboard"; the quizzes tab drops "& Practice" from
// the label (practice is a per-course teacher opt-in and hidden by default).
const COURSE_TAB_ITEMS: TabItem[] = [
  { value: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'materials', label: 'Materials', icon: BookOpen },
  { value: 'quizzes', label: 'Quizzes', icon: ListChecks },
  { value: 'results', label: 'Results', icon: History },
];

function CoursePage(): React.JSX.Element {
  return (
    <RequireAuth>
      <CourseContent />
    </RequireAuth>
  );
}

function CourseContent(): React.JSX.Element {
  const { courseId } = Route.useParams();
  const course = trpc.learning.courses.get.useQuery({ id: courseId });
  // SCH-AI-4: the floating "Ask your mentor" trigger + chat dock — gated by the
  // AI-tutor flag so coaching-centre tenants (aiTutorEnabled=false, Ishraf v1:
  // "hide Mentor / AI") never see the mentor entry point, matching the nav.
  const features = useResolvedFeatures();
  const mentorEnabled = features.data?.aiTutorEnabled === true;
  const [mentorOpen, setMentorOpen] = React.useState(false);
  const [tab, setTab] = React.useState<CourseTab>('overview');

  return (
    <LearnShell title={course.data?.title ?? 'Course'} wide>
      <div className="space-y-6">
        <Link
          to="/catalog"
          className="inline-flex min-h-touch items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Catalog
        </Link>

        {course.isPending ? (
          <Card className="card-soft">
            <CardContent className="space-y-3 p-6">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-8 w-72 max-w-full" />
              <Skeleton className="h-4 w-96 max-w-full" />
            </CardContent>
          </Card>
        ) : course.error ? (
          <EmptyState title="Couldn’t load this course" description={course.error.message} />
        ) : !course.data ? (
          <EmptyState
            title="Course not found"
            description="This course doesn’t exist or is no longer available."
            action={
              <Button asChild variant="outline">
                <Link to="/catalog">Back to courses</Link>
              </Button>
            }
          />
        ) : (
          <Card className="card-soft animate-fade-in-up overflow-hidden">
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="gap-1.5">
                  <GraduationCap className="size-3.5" aria-hidden="true" />
                  {kindLabelOf(course.data.kind)}
                </Badge>
              </div>
              <header className="space-y-2">
                <h2 className="text-2xl font-semibold tracking-tight">{course.data.title}</h2>
                {course.data.description ? (
                  <p className="max-w-prose text-sm leading-relaxed text-muted-foreground">
                    {course.data.description}
                  </p>
                ) : null}
              </header>
            </CardContent>
          </Card>
        )}

        {course.data ? (
          <div className="space-y-6">
            <Tabs
              ariaLabel="Course sections"
              value={tab}
              onValueChange={(v) => setTab(v as CourseTab)}
              items={COURSE_TAB_ITEMS}
            />
            {tab === 'overview' ? (
              <OverviewSection
                courseId={courseId}
                kindLabel={kindLabelOf(course.data.kind)}
                onStartQuiz={() => setTab('quizzes')}
              />
            ) : null}
            {tab === 'materials' ? <ContentSection courseId={courseId} /> : null}
            {tab === 'quizzes' ? (
              <div className="space-y-6">
                {/* SCH-I3-C1: Practice is a per-course teacher opt-in. */}
                {course.data.practiceEnabled ? <PracticeSection courseId={courseId} /> : null}
                <QuizzesSection courseId={courseId} />
              </div>
            ) : null}
            {tab === 'results' ? <ResultsSection courseId={courseId} /> : null}
          </div>
        ) : null}
      </div>

      {course.data && mentorEnabled ? (
        <>
          {/* Floating mentor trigger — bottom-right, above content, below the dock. */}
          <Button
            data-testid="mentor-open"
            onClick={() => setMentorOpen(true)}
            className="float-single min-h-touch rounded-full shadow-lg"
          >
            <MessageCircle className="size-4" aria-hidden="true" />
            Ask your mentor
          </Button>
          <MentorDock open={mentorOpen} courseId={courseId} onClose={() => setMentorOpen(false)} />
        </>
      ) : null}
    </LearnShell>
  );
}

// ---------------------------------------------------------------------------
// Course content (authored items)
// ---------------------------------------------------------------------------

/**
 * Course content as the learner sees it — driven by the GATED
 * `learning.learner.materials.courseContent` shape `{ items, modules }`.
 *
 * `modules` is the authoritative top-level module set (each with lock state +
 * reason); `items` carries ONLY content the learner may see (locked-module
 * content is stripped server-side). `<CourseModules>` renders the module
 * accordion: unlocked modules pull children from `items` by `parentItemId`;
 * locked modules show a lock badge + reason and never expand / never fetch a
 * file. Loose top-level items render below as standalone cards.
 */
function ContentSection({ courseId }: { courseId: string }): React.JSX.Element | null {
  // Published-only, learner-scoped, gated module set + visible items.
  const contentQ = trpc.learning.learner.materials.courseContent.useQuery({ courseId });

  if (contentQ.isPending) {
    return (
      <section className="space-y-3">
        <SectionHeading icon={BookOpen}>Course content</SectionHeading>
        <div className="space-y-3">
          {Array.from({ length: 2 }, (_, i) => (
            <Card key={i} className="card-soft">
              <CardContent className="space-y-3 p-5">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  }

  const modules = contentQ.data?.modules ?? [];
  const items = contentQ.data?.items ?? [];
  const looseItemCount = items.filter((it) => it.parentItemId == null && it.kind !== 'unit').length;

  return (
    <section className="space-y-3" data-testid="course-content">
      <SectionHeading icon={BookOpen}>Course materials</SectionHeading>
      {contentQ.error ? (
        <EmptyState title="Couldn’t load materials" description={contentQ.error.message} />
      ) : modules.length === 0 && looseItemCount === 0 ? (
        <EmptyState
          title="No materials yet"
          description="When your instructor adds lessons, readings, or files for this course, they’ll show up here."
        />
      ) : (
        <CourseModules modules={modules} items={items} />
      )}
    </section>
  );
}

/**
 * Shared section heading — an icon tile + title, matching the prototype's
 * section-head rhythm. Presentational only.
 */
function SectionHeading({
  icon: Icon,
  children,
}: {
  icon: typeof BookOpen;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
      <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      {children}
    </h3>
  );
}

// ---------------------------------------------------------------------------
// Practice generator (P-SCH-D / learning.attempts.generatePractice)
// ---------------------------------------------------------------------------

/** The four practice modes — mirrors the `generatePractice` input union. */
const PRACTICE_MODES = [
  { key: 'chapter', label: 'Chapter' },
  { key: 'mixed', label: 'Mixed' },
  { key: 'yearly', label: 'Yearly' },
  { key: 'difficulty', label: 'Difficulty' },
] as const;
type PracticeMode = (typeof PRACTICE_MODES)[number]['key'];

const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;
type Difficulty = (typeof DIFFICULTIES)[number];

/**
 * "Practice" — generate a personal, ephemeral practice attempt from the
 * course's question banks (`learning.attempts.generatePractice`) and jump
 * straight into the runner. Mode picker (Chapter / Mixed / Yearly /
 * Difficulty) + the mode-specific filter + a question count, then "Start
 * practice". The generated quiz is flagged `metadata.practice` server-side so
 * it never shows in the catalog quiz list.
 *
 * Chapter offers a native select of the distinct skill-tags across the course's
 * question banks (`learning.attempts.courseSkillTags`); when no questions are
 * tagged yet it falls back to a free-text tag so the learner is never stranded.
 * The other modes use a native select / number input. Mobile-first: filters
 * stack full-width below `sm`.
 */
function PracticeSection({ courseId }: { courseId: string }): React.JSX.Element {
  const navigate = useNavigate();
  const generate = trpc.learning.attempts.generatePractice.useMutation();

  // P-SCH-V1 (ADR-0103): the Chapter-mode picker is driven by the FIRST-CLASS
  // chapters entity (`learning.chapters.listForCourse`), NOT free-text skill
  // tags. We send the chapter_id (uuid) to generatePractice, which now requires
  // `chapterId` (the old free-text `chapterTag` was removed — sending it was a
  // no-op that left chapter practice unscoped).
  const chaptersQ = trpc.learning.chapters.listForCourse.useQuery({ courseId });
  const chapters = chaptersQ.data?.chapters ?? [];
  const hasChapters = chapters.length > 0;

  const [mode, setMode] = React.useState<PracticeMode>('mixed');
  const [chapterId, setChapterId] = React.useState('');
  const [year, setYear] = React.useState('');
  const [difficulty, setDifficulty] = React.useState<Difficulty>('medium');
  const [count, setCount] = React.useState(10);

  // A current year ceiling so the year picker offers a sensible recent range.
  const currentYear = new Date().getFullYear();
  const years = React.useMemo(
    () => Array.from({ length: 20 }, (_, i) => currentYear - i),
    [currentYear],
  );

  const canStart =
    !generate.isPending &&
    count >= 1 &&
    count <= 50 &&
    (mode !== 'chapter' || chapterId !== '') &&
    (mode !== 'yearly' || year !== '');

  function handleStart(): void {
    if (!canStart) return;
    generate.mutate(
      {
        courseId,
        mode,
        count,
        ...(mode === 'chapter' ? { chapterId } : {}),
        ...(mode === 'yearly' ? { year: Number(year) } : {}),
        ...(mode === 'difficulty' ? { difficulty } : {}),
      },
      {
        onSuccess: (res) => {
          void navigate({
            to: '/quiz/$quizId',
            params: { quizId: res.quizId },
            search: { title: 'Practice' },
          });
        },
      },
    );
  }

  return (
    <section className="ls-stagger space-y-3" data-testid="practice-section">
      <SectionHeading icon={Dumbbell}>Practice</SectionHeading>
      <Card className="card-soft animate-fade-in">
        <CardContent className="space-y-5 p-5">
          <p className="text-sm text-muted-foreground">
            Build a custom set of questions to drill on — your practice runs are private and don’t
            affect the published tests.
          </p>

          {/* Mode picker */}
          <div className="space-y-1.5">
            <Label>Mode</Label>
            <div
              role="radiogroup"
              aria-label="Practice mode"
              className="grid grid-cols-2 gap-2 sm:grid-cols-4"
            >
              {PRACTICE_MODES.map((m) => {
                const active = m.key === mode;
                return (
                  <button
                    key={m.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setMode(m.key)}
                    data-testid={`practice-mode-${m.key}`}
                    className={cn(
                      'press flex min-h-touch items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-input bg-background text-muted-foreground hover:bg-muted/50',
                    )}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode-specific filter */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {mode === 'chapter' ? (
              <div className="space-y-1.5">
                <Label htmlFor="practice-chapter">Chapter</Label>
                {chaptersQ.isPending ? (
                  <div className="relative">
                    <select
                      id="practice-chapter"
                      disabled
                      data-testid="practice-chapter"
                      className="flex min-h-touch w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm text-muted-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Loading chapters…</option>
                    </select>
                    <ChevronDown
                      className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                ) : hasChapters ? (
                  <div className="relative">
                    <select
                      id="practice-chapter"
                      value={chapterId}
                      onChange={(e) => setChapterId(e.target.value)}
                      data-testid="practice-chapter"
                      className="flex min-h-touch w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Choose a chapter…</option>
                      {chapters.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.title}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                ) : chaptersQ.isError ? (
                  // Query FAILED (network / 5xx) — don't tell the learner the
                  // course has no chapters when we simply couldn't load them.
                  <div
                    data-testid="practice-chapter-error"
                    className="flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                  >
                    <span>Couldn&rsquo;t load chapters.</span>
                    <button
                      type="button"
                      className="shrink-0 font-medium underline underline-offset-2"
                      onClick={() => void chaptersQ.refetch()}
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  // No chapters defined for this course yet — the teacher adds
                  // them in the admin Chapters tab. Other practice modes still work.
                  <p
                    data-testid="practice-chapter-empty"
                    className="rounded-md border border-dashed border-input bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                  >
                    No chapters yet — try Mixed or Difficulty mode, or ask your teacher to add
                    chapters.
                  </p>
                )}
              </div>
            ) : null}

            {mode === 'yearly' ? (
              <div className="space-y-1.5">
                <Label htmlFor="practice-year">Exam year</Label>
                <div className="relative">
                  <select
                    id="practice-year"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    data-testid="practice-year"
                    className="flex min-h-touch w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Choose a year…</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
              </div>
            ) : null}

            {mode === 'difficulty' ? (
              <div className="space-y-1.5">
                <Label htmlFor="practice-difficulty">Difficulty</Label>
                <div className="relative">
                  <select
                    id="practice-difficulty"
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                    data-testid="practice-difficulty"
                    className="flex min-h-touch w-full appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm capitalize ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {DIFFICULTIES.map((d) => (
                      <option key={d} value={d} className="capitalize">
                        {d}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="practice-count">Number of questions</Label>
              <Input
                id="practice-count"
                type="number"
                inputMode="numeric"
                min={1}
                max={50}
                value={count}
                onChange={(e) => {
                  const n = Number.parseInt(e.target.value, 10);
                  setCount(Number.isFinite(n) ? Math.min(50, Math.max(1, n)) : 1);
                }}
                data-testid="practice-count"
                className="max-w-32"
              />
            </div>
          </div>

          {generate.error ? (
            <p className="text-sm text-destructive" role="alert">
              {generate.error.message}
            </p>
          ) : null}

          <Button
            className="press min-h-touch w-full sm:w-auto sm:px-8"
            onClick={handleStart}
            disabled={!canStart}
            data-testid="practice-start"
          >
            <Dumbbell className="size-4" aria-hidden="true" />
            {generate.isPending ? 'Building…' : 'Start practice'}
          </Button>
        </CardContent>
      </Card>
    </section>
  );
}

/** SCH-I3-C6 quiz-list sort keys. */
type QuizSortKey = 'newest' | 'oldest' | 'title-asc' | 'title-desc';
const QUIZ_SORT_OPTIONS: ReadonlyArray<{ value: QuizSortKey; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'title-asc', label: 'Name A–Z' },
  { value: 'title-desc', label: 'Name Z–A' },
];

function QuizzesSection({ courseId }: { courseId: string }): React.JSX.Element {
  const quizzes = trpc.learning.quizzes.listForLearner.useQuery({ courseId });
  const allRows = quizzes.data?.quizzes ?? [];
  // Ishraf §3.1 — the Chapter options come from the course's OWN chapters, so
  // adding or renaming one on the teacher side changes this list with no code
  // change. Never a hard-coded list.
  const chaptersQ = trpc.learning.chapters.listForCourse.useQuery({ courseId });
  // Attempted / Reviewed are facts about THIS learner's attempts, so they ride
  // on the same history query the Results tab uses — including its per-attempt
  // `reviewStatus` (§3.6), which is where the Reviewed vocabulary is defined.
  const attemptsQ = trpc.learning.learner.attempts.list.useQuery({ courseId, limit: 100 });

  // SCH-I3-C6: client-side name search + sort (upload date / name) over the
  // published quiz list. The list is a small per-course set, so no server call.
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<QuizSortKey>('newest');
  const [chapterId, setChapterId] = React.useState<string>('');
  const [year, setYear] = React.useState<string>('');
  const [attempted, setAttempted] = React.useState<'' | 'yes' | 'no'>('');
  const [reviewed, setReviewed] = React.useState<'' | 'reviewed' | 'not'>('');

  /**
   * Ishraf §3.1 — the Year options are the DISTINCT exam years actually tagged
   * on THIS course's papers (`quizzes.year`), newest first. Never a generated
   * range: offering 2019 for a course whose oldest paper is 2023 would let a
   * learner filter their way to a guaranteed empty list. A course where no quiz
   * carries a year renders no control at all (see the filter row below).
   */
  const years = React.useMemo(() => {
    const seen = new Set<number>();
    for (const r of allRows) if (r.year != null) seen.add(r.year);
    return [...seen].sort((a, b) => b - a);
  }, [allRows]);

  /**
   * Per-quiz rollup of this learner's attempts.
   *
   *  - attempted — at least one attempt of any status, which is exactly how
   *    Ishraf words it ("attempted at least once").
   *  - reviewStatus — a quiz counts as Not Reviewed while ANY of its finished
   *    attempts is still pending, so one unmarked essay keeps the quiz in the
   *    Not Reviewed bucket rather than being hidden by a later clean attempt.
   *    A quiz with no finished attempt has no review state at all and is
   *    excluded whenever the Reviewed filter is set — "not reviewed" would
   *    imply a teacher owes something for work never handed in.
   */
  const byQuiz = React.useMemo(() => {
    const map = new Map<string, { attempted: boolean; finished: number; pending: number }>();
    for (const a of attemptsQ.data?.items ?? []) {
      const cur = map.get(a.quizId) ?? { attempted: false, finished: 0, pending: 0 };
      cur.attempted = true;
      if (a.status === 'graded' || a.status === 'submitted') {
        cur.finished += 1;
        if (a.reviewStatus === 'pending') cur.pending += 1;
      }
      map.set(a.quizId, cur);
    }
    return map;
  }, [attemptsQ.data]);

  const rows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = q ? allRows.filter((r) => r.title.toLowerCase().includes(q)) : allRows;
    if (chapterId) filtered = filtered.filter((r) => r.chapterId === chapterId);
    // An UNTAGGED paper (year null) only disappears while a year is actively
    // chosen — it must stay in the default list, not vanish because some
    // sibling quiz happens to carry a year.
    if (year) filtered = filtered.filter((r) => r.year != null && String(r.year) === year);
    if (attempted) {
      const want = attempted === 'yes';
      filtered = filtered.filter((r) => (byQuiz.get(r.id)?.attempted ?? false) === want);
    }
    if (reviewed) {
      filtered = filtered.filter((r) => {
        const st = byQuiz.get(r.id);
        if (!st || st.finished === 0) return false;
        return reviewed === 'reviewed' ? st.pending === 0 : st.pending > 0;
      });
    }
    const byDate = (a: (typeof allRows)[number], b: (typeof allRows)[number]): number =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return [...filtered].sort((a, b) => {
      switch (sort) {
        case 'oldest':
          return byDate(a, b);
        case 'newest':
          return -byDate(a, b);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
      }
    });
  }, [allRows, search, sort, chapterId, year, attempted, reviewed, byQuiz]);

  return (
    <section className="space-y-4">
      <SectionHeading icon={ListChecks}>Tests &amp; quizzes</SectionHeading>

      {allRows.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tests by name…"
            aria-label="Search tests"
            data-testid="quiz-search"
            className="sm:flex-1"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as QuizSortKey)}
            aria-label="Sort tests"
            data-testid="quiz-sort"
            className="press min-h-touch rounded-md border border-input bg-background px-3 py-2 text-sm font-medium sm:w-44"
          >
            {QUIZ_SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {allRows.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid="quiz-filters">
          {/* Chapter options are the course's own chapters — dynamic by
              construction, so a chapter added on the teacher side appears here
              with no code change (Ishraf §3.1). Hidden entirely for a course
              that has no chapters, rather than showing an empty control. */}
          {(chaptersQ.data?.chapters ?? []).length > 0 ? (
            <select
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              aria-label="Filter by chapter"
              data-testid="quiz-filter-chapter"
              className="press min-h-touch rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
            >
              <option value="">All chapters</option>
              {(chaptersQ.data?.chapters ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          ) : null}
          {/* Year sits beside Chapter and is hidden — not disabled-and-empty —
              for a course whose papers carry no year at all. */}
          {years.length > 0 ? (
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              aria-label="Filter by exam year"
              data-testid="quiz-filter-year"
              className="press min-h-touch rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
            >
              <option value="">All years</option>
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          ) : null}
          <select
            value={attempted}
            onChange={(e) => setAttempted(e.target.value as '' | 'yes' | 'no')}
            aria-label="Filter by attempted"
            data-testid="quiz-filter-attempted"
            className="press min-h-touch rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
          >
            <option value="">Attempted: any</option>
            <option value="yes">Attempted</option>
            <option value="no">Not attempted</option>
          </select>
          <select
            value={reviewed}
            onChange={(e) => setReviewed(e.target.value as '' | 'reviewed' | 'not')}
            aria-label="Filter by reviewed"
            data-testid="quiz-filter-reviewed"
            className="press min-h-touch rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
          >
            <option value="">Reviewed: any</option>
            <option value="reviewed">Reviewed</option>
            <option value="not">Not reviewed</option>
          </select>
          {chapterId || year || attempted || reviewed ? (
            <Button
              variant="ghost"
              size="sm"
              className="press min-h-touch"
              data-testid="quiz-filter-clear"
              onClick={() => {
                setChapterId('');
                setYear('');
                setAttempted('');
                setReviewed('');
              }}
            >
              Clear filters
            </Button>
          ) : null}
          {/* One count for every control above it — search, chapter, year,
              attempted and reviewed all narrow the same `rows`. */}
          <span
            className="ml-auto self-center text-xs text-muted-foreground"
            data-testid="quiz-filter-count"
          >
            Showing {rows.length} of {allRows.length}
          </span>
        </div>
      ) : null}

      {quizzes.isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }, (_, i) => (
            <Card key={i} className="card-soft">
              <CardContent className="space-y-3 p-5">
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-11 w-full sm:w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : quizzes.error ? (
        <EmptyState title="Couldn’t load tests" description={quizzes.error.message} />
      ) : allRows.length === 0 ? (
        <EmptyState
          title="No tests published yet"
          description="When the instructor publishes a test for this course, it’ll show up here."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No tests match your filters"
          description="Try a different name, chapter, or status."
        />
      ) : (
        <div className="ls-stagger space-y-3">
          {rows.map((quiz) => (
            <QuizRow key={quiz.id} quiz={quiz} />
          ))}
        </div>
      )}
    </section>
  );
}

function QuizRow({ quiz }: { quiz: Quiz }): React.JSX.Element {
  const minutes =
    quiz.timeLimitSeconds != null && quiz.timeLimitSeconds > 0
      ? Math.round(quiz.timeLimitSeconds / 60)
      : null;

  return (
    <Card data-testid="quiz-row" className="card-soft card-lift press">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 gap-3.5">
            <span
              className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-brand-blue/15 bg-brand-blue/10 text-brand-blue"
              aria-hidden="true"
            >
              <ListChecks className="size-5" />
            </span>
            <div className="min-w-0 space-y-1.5">
              <p className="font-semibold leading-snug tracking-tight">{quiz.title}</p>
              {quiz.description ? (
                <p className="text-sm text-muted-foreground">{quiz.description}</p>
              ) : null}
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                  <ListChecks className="size-3.5" aria-hidden="true" />
                  {quiz.questionCount} {quiz.questionCount === 1 ? 'question' : 'questions'}
                </Badge>
                {minutes != null ? (
                  <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                    <Clock className="size-3.5" aria-hidden="true" />
                    {minutes} min
                  </Badge>
                ) : null}
                {quiz.passingScore != null ? (
                  <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                    <Target className="size-3.5" aria-hidden="true" />
                    Pass ≥ {quiz.passingScore}%
                  </Badge>
                ) : null}
                {quiz.attemptLimit != null ? (
                  <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                    <Repeat className="size-3.5" aria-hidden="true" />
                    {quiz.attemptLimit} {quiz.attemptLimit === 1 ? 'attempt' : 'attempts'}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
          <Button
            asChild
            className="press min-h-touch w-full shrink-0 sm:w-auto sm:px-8"
            data-testid="start-quiz-button"
          >
            <Link to="/quiz/$quizId" params={{ quizId: quiz.id }}>
              <PlayCircle className="size-4" aria-hidden="true" />
              Start
            </Link>
          </Button>
        </div>
        <AttemptHistory quizId={quiz.id} />
      </CardContent>
    </Card>
  );
}

function AttemptHistory({ quizId }: { quizId: string }): React.JSX.Element | null {
  const attempts = trpc.learning.attempts.list.useQuery({ quizId });
  const rows = attempts.data?.attempts ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="space-y-1 border-t pt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Your attempts
      </p>
      <ul className="divide-y">
        {rows.map((attempt) => (
          <li key={attempt.id}>
            <Link
              to="/result/$attemptId"
              params={{ attemptId: attempt.id }}
              className="-mx-2 flex min-h-touch flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40"
              data-testid="attempt-history-row"
            >
              <span className="font-medium">Attempt {attempt.attemptNumber}</span>
              <ScoreChip score={attempt.scorePercent} />
              <AttemptStatusBadge attempt={attempt} />
              <span className="ml-auto text-xs text-muted-foreground">
                {formatAttemptDate(attempt)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The prototype's `.score-chip` — a tabular-nums percentage pill toned by
 * the platform score convention (>=80 success, 50-79 warning, <50 destructive).
 * A null score (in-progress / ungraded) renders a calm neutral dash.
 */
/**
 * Ishraf §3.6 — whether a human still owes this attempt a mark.
 *
 * Only rendered for finished attempts: an in-progress attempt has no result to
 * review, and tagging one "Reviewed" would be a lie. The server derives the
 * value (see `reviewStatus` on the attempt DTO); this only paints it.
 */
function ReviewChip({ status }: { status: 'reviewed' | 'pending' }): React.JSX.Element {
  const pending = status === 'pending';
  return (
    <span
      data-testid={pending ? 'review-pending' : 'review-reviewed'}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        pending ? 'bg-warning/15 text-warning' : 'bg-primary/10 text-primary',
      )}
    >
      {pending ? (
        <Clock className="size-3" aria-hidden="true" />
      ) : (
        <CheckCircle2 className="size-3" aria-hidden="true" />
      )}
      {pending ? 'Pending Reviewed' : 'Reviewed'}
    </span>
  );
}

function ScoreChip({ score }: { score: number | null }): React.JSX.Element {
  if (score == null) {
    return (
      <span className="inline-flex min-w-11 items-center justify-center rounded-full bg-muted/60 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
        —
      </span>
    );
  }
  const rounded = Math.round(score);
  const tone =
    rounded >= 80
      ? 'bg-primary/12 text-primary'
      : rounded >= 50
        ? 'bg-warning/15 text-warning'
        : 'bg-destructive/12 text-destructive';
  return (
    <span
      className={cn(
        'inline-flex min-w-11 items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums',
        tone,
      )}
      aria-label={`Score ${rounded} percent`}
    >
      {rounded}%
    </span>
  );
}

function AttemptStatusBadge({ attempt }: { attempt: Attempt }): React.JSX.Element {
  if (attempt.status === 'in_progress') return <Badge variant="secondary">In progress</Badge>;
  if (attempt.passed === true) return <Badge variant="success">Passed</Badge>;
  if (attempt.passed === false) return <Badge variant="destructive">Failed</Badge>;
  if (attempt.status === 'submitted') return <Badge variant="warning">Pending grading</Badge>;
  // graded with no passing score configured on the quiz → no pass/fail verdict.
  return <Badge variant="outline">Completed</Badge>;
}

/** Wrapped in `new Date(...)` so an offline-persisted (string-revived) cache row still formats. */
function formatAttemptDate(attempt: Attempt): string {
  const d = new Date(attempt.submittedAt ?? attempt.startedAt);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Overview tab — a LIGHT, course-scoped summary built from data we already have
// (quizzes + chapters + the learner's own course-filtered attempts). No new
// analytics backend (the deeper /progress dashboard stays, just hidden from nav).
// ---------------------------------------------------------------------------

type LearnerAttempt = RouterOutputs['learning']['learner']['attempts']['list']['items'][number];
/** Ticket 3.3 — the caller's cohort standing, straight off the contract. */
type LearnerCourseRank = RouterOutputs['learning']['learner']['attempts']['courseRank'];

function isoDateOf(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  valueClassName,
  testId,
}: {
  icon: typeof Target;
  label: string;
  value: React.ReactNode;
  hint?: string;
  /** Tone for the value line — the rank band colour is the only user today. */
  valueClassName?: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Card className="card-soft" data-testid={testId}>
      <CardContent className="space-y-1 p-4">
        <p className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Icon className="size-3.5" aria-hidden="true" />
          {label}
        </p>
        <p className={cn('text-2xl font-bold tabular-nums leading-tight', valueClassName)}>
          {value}
        </p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

/** Round to 1 decimal for a "12.4s" speed reading; whole seconds show clean. */
function formatSeconds(sec: number | null): string {
  if (sec == null) return '—';
  return Number.isInteger(sec) ? `${sec}s` : `${sec.toFixed(1)}s`;
}

/**
 * Ticket 3.4 — "8 hrs 42 mins" / "42 mins" / "8 hrs".
 *
 * Minutes are FLOORED, so the tile never claims time that wasn't recorded; a
 * non-zero total under a minute reads "<1 min" rather than collapsing to a flat
 * zero that looks like nothing was tracked.
 */
function formatStudyTime(seconds: number): string {
  if (seconds <= 0) return '0 mins';
  const totalMinutes = Math.floor(seconds / 60);
  if (totalMinutes === 0) return '<1 min';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const hrs = h > 0 ? `${h} ${h === 1 ? 'hr' : 'hrs'}` : '';
  const mins = m > 0 ? `${m} ${m === 1 ? 'min' : 'mins'}` : '';
  return h > 0 && m > 0 ? `${hrs} ${mins}` : hrs || mins;
}

/**
 * Ticket 3.2 — the learner's streak, on the course dashboard.
 *
 * PER-COURSE, by Ishraf's ruling of 2026-08-20: "the streak for the course
 * should show if they attempted quizzes from that course daily or not." It
 * counts days in a row with a graded attempt on THIS course's papers, so a
 * student taking Physics and Chemistry sees two independent numbers. That is
 * deliberately NOT the account-wide `learning_streaks.quiz` counter the
 * Momentum card on /profile shows — that one answers "did they show up at
 * all", and would read identically on every course page.
 *
 * `streakDays` rides on the `courseStats` query the dashboard already makes,
 * so the tile costs no extra round trip. The caller still owns the
 * `gamificationEnabled` gate (hide, never delete — ADR-0103), but the value no
 * longer depends on `gamification.summary`, which FORBIDs the call outright
 * for a tenant with gamification off.
 */
function StudyStreakTile({
  enabled,
  days,
  loaded,
}: {
  enabled: boolean;
  days: number;
  loaded: boolean;
}): React.JSX.Element | null {
  if (!enabled) return null;
  return (
    <StatCard
      icon={Flame}
      label="Study streak"
      value={loaded ? `${days} ${days === 1 ? 'day' : 'days'}` : '—'}
      hint="Days in a row with a test in this course"
      testId="course-study-streak"
    />
  );
}

/**
 * Ticket 3.3 — where the learner stands in this course's cohort.
 *
 * "Top 12%" is the COMPLEMENT of the server's percentile rank (the share of the
 * cohort at or below the learner), which is why the raw number isn't printed.
 * The colour comes from the server's `band`, decided against the tenant's own
 * `bands` thresholds — deliberately not 15/75 constants in this file, so a
 * centre that moves its cut-offs recolours the tile with no deploy.
 *
 * Every non-`ok` case renders honestly rather than inventing a standing: no
 * attempts yet is NA, and a cohort under the tenant's k-anonymity floor stays
 * suppressed (the server won't even send the size).
 */
function CourseRankTile({
  rank,
  isPending,
  isError,
}: {
  rank: LearnerCourseRank | undefined;
  isPending: boolean;
  isError: boolean;
}): React.JSX.Element {
  // In flight, failed, or 404 (the proc NOT_FOUNDs for a course the caller
  // isn't actively enrolled on) — a calm dash, never a made-up rank.
  if (isPending || isError || !rank) {
    return (
      <StatCard
        icon={Trophy}
        label="Course rank"
        value="—"
        hint={isError ? 'Rank unavailable right now' : 'Working out your standing…'}
        testId="course-rank"
      />
    );
  }
  if (rank.availability === 'no_attempts') {
    return (
      <StatCard
        icon={Trophy}
        label="Course rank"
        value="NA"
        hint="Sit a test to get ranked"
        testId="course-rank"
      />
    );
  }
  if (rank.availability === 'cohort_too_small') {
    return (
      <StatCard
        icon={Trophy}
        label="Course rank"
        value="—"
        hint={
          rank.meanScorePercent == null
            ? 'Too few classmates to rank against yet'
            : `Too few classmates to rank against · you average ${rank.meanScorePercent}%`
        }
        testId="course-rank"
      />
    );
  }
  // A percentile of 99.5 rounds to 100, which would print "Top 0%" — clamp.
  const topPercent = Math.max(1, 100 - (rank.percentile ?? 0));
  const tone =
    rank.band === 'high' ? 'text-success' : rank.band === 'low' ? 'text-destructive' : undefined;
  return (
    <StatCard
      icon={Trophy}
      label="Course rank"
      value={`Top ${topPercent}%`}
      valueClassName={tone}
      hint={
        rank.rank != null && rank.cohortSize != null
          ? `#${rank.rank} of ${rank.cohortSize} students`
          : undefined
      }
      testId="course-rank"
    />
  );
}

/** Exported for its component test (course-overview.test.tsx), as `IntroCard` is. */
export function OverviewSection({
  courseId,
  kindLabel,
  onStartQuiz,
}: {
  courseId: string;
  kindLabel: string;
  onStartQuiz: () => void;
}): React.JSX.Element {
  const quizzesQ = trpc.learning.quizzes.listForLearner.useQuery({ courseId });
  const chaptersQ = trpc.learning.chapters.listForCourse.useQuery({ courseId });
  const attemptsQ = trpc.learning.learner.attempts.list.useQuery({ courseId, limit: 100 });
  // SCH-I3-C5: avg answer speed (mean elapsed_ms across this course's attempts);
  // ticket 3.4 rides the same proc for the total recorded quiz time.
  const statsQ = trpc.learning.learner.attempts.courseStats.useQuery({ courseId });
  // Ticket 3.3: cohort standing. Own data + a cohort size only — the server
  // suppresses everything comparative below the tenant's k-anonymity floor.
  const rankQ = trpc.learning.learner.attempts.courseRank.useQuery({ courseId });
  // Ticket 3.2: the streak tile rides the same `gamificationEnabled` gate as
  // <MomentumCard> on /profile, so a coaching centre with gamification off
  // sees no streak anywhere.
  const features = useResolvedFeatures();
  const streakEnabled = features.data?.gamificationEnabled === true;

  const quizzes = quizzesQ.data?.quizzes ?? [];
  const chapters = chaptersQ.data?.chapters ?? [];
  const items = attemptsQ.data?.items ?? [];
  const totalAttempts = attemptsQ.data?.total ?? items.length;
  const questionCount = quizzes.reduce((sum, q) => sum + q.questionCount, 0);
  // Not loaded yet reads "—", never 0. The `?? []` above alone turned a page
  // that was still loading into an empty course for a moment (#655); a real 0
  // shows once the data is in.
  const quizzesLoaded = quizzesQ.data != null;
  const chaptersLoaded = chaptersQ.data != null;
  const attemptsLoaded = attemptsQ.data != null;

  const graded = items.filter(
    (a): a is LearnerAttempt & { scorePercent: number } => a.scorePercent != null,
  );
  const avg = graded.length
    ? Math.round(graded.reduce((s, a) => s + a.scorePercent, 0) / graded.length)
    : null;
  const best = graded.length ? Math.round(Math.max(...graded.map((a) => a.scorePercent))) : null;
  const recent = items.slice(0, 5);

  // SCH-I3-C5: quizzes completed = distinct published quizzes with at least one
  // finished (submitted/graded) attempt, over the count of published quizzes.
  const completedQuizIds = new Set(
    items.filter((a) => a.status === 'graded' || a.status === 'submitted').map((a) => a.quizId),
  );
  const completedCount = completedQuizIds.size;
  const pctComplete =
    quizzesLoaded && attemptsLoaded && quizzes.length > 0
      ? Math.round((completedCount / quizzes.length) * 100)
      : null;
  const avgSpeed = statsQ.data?.avgSecondsPerQuestion ?? null;

  return (
    <section className="space-y-5" data-testid="course-dashboard">
      <SectionHeading icon={LayoutDashboard}>Dashboard</SectionHeading>

      {/* Ishraf §3.2 / §3.3 / §3.4 — streak, standing and time on top, where a
          student looks first. Full-width on a phone: these values are short
          phrases ("8 hrs 42 mins"), not the two-character numbers below. */}
      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          streakEnabled ? 'sm:grid-cols-3' : 'sm:grid-cols-2',
        )}
        data-testid="course-dashboard-highlights"
      >
        <StudyStreakTile
          enabled={streakEnabled}
          days={statsQ.data?.streakDays ?? 0}
          loaded={statsQ.data != null}
        />
        <CourseRankTile rank={rankQ.data} isPending={rankQ.isPending} isError={rankQ.isError} />
        <StatCard
          icon={Hourglass}
          // The honest label the /home KPI strip already uses — this is time
          // inside the quiz runner, not time spent in the course.
          label="Quiz time"
          value={statsQ.data ? formatStudyTime(statsQ.data.studySeconds) : '—'}
          hint="Time in tests only — for now"
          testId="course-study-time"
        />
      </div>

      {/* Headline progress — the numbers the owner asked to surface first. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          icon={CheckCircle2}
          label="Quizzes completed"
          value={pctComplete == null ? '—' : `${pctComplete}%`}
          hint={
            quizzesLoaded && attemptsLoaded
              ? `${completedCount} of ${quizzes.length} done`
              : undefined
          }
          testId="course-stat-completed"
        />
        <StatCard
          icon={Target}
          label="Highest grade"
          value={best == null ? '—' : `${best}%`}
          hint={avg == null ? undefined : `avg ${avg}%`}
        />
        <StatCard
          icon={Clock}
          label="Avg speed / question"
          value={formatSeconds(avgSpeed)}
          hint={statsQ.data?.timedAnswers ? `over ${statsQ.data.timedAnswers} answers` : undefined}
        />
        <StatCard
          icon={History}
          label="Attempts"
          value={attemptsLoaded ? totalAttempts : '—'}
          testId="course-stat-attempts"
        />
      </div>

      {/* Secondary content stats. */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          icon={ListChecks}
          label="Quizzes"
          value={quizzesLoaded ? quizzes.length : '—'}
          testId="course-stat-quizzes"
        />
        <StatCard
          icon={Layers}
          label="Chapters"
          value={chaptersLoaded ? chapters.length : '—'}
          testId="course-stat-chapters"
        />
        <StatCard
          icon={Hash}
          label="Questions"
          value={quizzesLoaded ? questionCount : '—'}
          testId="course-stat-questions"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_300px] lg:items-start">
        <Card className="card-soft">
          <CardContent className="space-y-3 p-5">
            <p className="text-sm font-semibold">Recent results</p>
            {attemptsQ.isPending ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} className="h-9 w-full" />
                ))}
              </div>
            ) : recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No attempts yet — start a quiz to see your results here.
              </p>
            ) : (
              <ul className="divide-y">
                {recent.map((a) => (
                  <li key={a.attemptId}>
                    <Link
                      to="/result/$attemptId"
                      params={{ attemptId: a.attemptId }}
                      className="-mx-2 flex min-h-touch flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted/40"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium">{a.quizTitle}</span>
                      <ScoreChip score={a.scorePercent} />
                      <span className="text-xs text-muted-foreground">
                        {isoDateOf(a.submittedAt)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="card-soft">
          <CardContent className="space-y-3 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              About this course
            </p>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <GraduationCap className="size-4" aria-hidden="true" />
                Type
              </span>
              <span className="font-medium">{kindLabel}</span>
            </div>
            <Separator />
            <p className="text-xs text-muted-foreground">
              Take your tests in the Quizzes tab, then track how you’re doing right here.
            </p>
            {/* SCH-I3-C5: practice removed from the dashboard — a Start-quiz CTA. */}
            <Button
              className="press w-full"
              onClick={onStartQuiz}
              data-testid="dashboard-start-quiz"
            >
              <PlayCircle className="size-4" aria-hidden="true" />
              Start a quiz
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Results tab — the learner's own attempts for THIS course (course-scoped via
// `learning.learner.attempts.list`, the same proc the global /results uses).
// ---------------------------------------------------------------------------

function ResultsSection({ courseId }: { courseId: string }): React.JSX.Element {
  const attemptsQ = trpc.learning.learner.attempts.list.useQuery({ courseId, limit: 50 });
  const items = attemptsQ.data?.items ?? [];
  const total = attemptsQ.data?.total ?? items.length;

  return (
    <section className="space-y-3" data-testid="course-results">
      <SectionHeading icon={History}>Your results</SectionHeading>
      {attemptsQ.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : attemptsQ.error ? (
        <EmptyState title="Couldn’t load results" description={attemptsQ.error.message} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No results yet"
          description="Take a quiz or a practice set and your graded attempts will appear here."
        />
      ) : (
        <Card className="card-soft">
          <CardContent className="p-2 sm:p-3">
            <ul className="divide-y">
              {items.map((a) => (
                <li key={a.attemptId}>
                  <Link
                    to="/result/$attemptId"
                    params={{ attemptId: a.attemptId }}
                    className="flex min-h-touch flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-2 py-2.5 text-sm transition-colors hover:bg-muted/40"
                    data-testid="course-result-row"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{a.quizTitle}</span>
                    <ScoreChip score={a.scorePercent} />
                    {a.status === 'graded' || a.status === 'submitted' ? (
                      <ReviewChip status={a.reviewStatus} />
                    ) : null}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {isoDateOf(a.submittedAt)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            {total > items.length ? (
              <p className="px-2 pt-2 text-xs text-muted-foreground">
                Showing the latest {items.length} of {total} attempts.
              </p>
            ) : null}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
